import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useRoute from '../../hooks/routesHook';
import { SET_WORKSPACE_SETUP_BANNER_MINIMIZED, getWorkspaceSetupBannerMinimized } from '../../states/slices/globalSlice';
import { useOnboardingTasks } from '../../hooks/useOnboardingTasks';

const WorkspaceSetupBannerMinimized: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const isMinimized = useSelector(getWorkspaceSetupBannerMinimized);
  
  // Call expensive hook only after basic checks
  const { completedTasks, totalTasks, completionPercentage, isOnboardingComplete } = useOnboardingTasks();

  // Don't render if onboarding is complete or no tasks
  if (isOnboardingComplete || totalTasks === 0) {
    return null;
  }

  const handleToggle = () => {
    const willExpand = isMinimized;

    // Toggle the banner state
    dispatch(SET_WORKSPACE_SETUP_BANNER_MINIMIZED(!isMinimized));

    // Only navigate when expanding, not when collapsing
    if (willExpand) {
      navigate(`${getDashboardBase()}/dashboard`);
    }
  };

  return (
    <button
      onClick={handleToggle}
      className="w-full max-w-[250px] bg-white dark:bg-darkBg border border-gray-200 dark:border-darkBg-border rounded-lg shadow-sm hover:shadow-md transition-all duration-200 p-1 cursor-pointer group"
      aria-label="Toggle workspace setup"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Progress indicator */}
          <div className="relative flex-shrink-0">
            <svg className="w-6 h-6 transform -rotate-90">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                className="text-gray-200 dark:text-gray-600"
              />
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 10}`}
                strokeDashoffset={`${2 * Math.PI * 10 * (1 - completionPercentage / 100)}`}
                className="text-orange-500 transition-all duration-300 ease-out"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[10px] font-semibold text-gray-700 dark:text-darkBg-mainText">
                {completionPercentage}%
              </span>
            </div>
          </div>

          {/* Text content */}
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-darkBg-mainText">
              Complete your setup
            </h3>
            <p className="text-xs text-gray-500 dark:text-darkBg-subText">
              {totalTasks - completedTasks} {totalTasks === 1 ? 'step' : 'steps'} remaining
            </p>
          </div>
        </div>

        {/* Expand indicator */}
        {/* <div className="flex items-center space-x-2">
          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            Expand
          </span>
          <svg 
            className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div> */}
      </div>
    </button>
  );
};

export default WorkspaceSetupBannerMinimized;
